import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

// The material family (shared by the clog body and strap GLBs) that gets tinted live. Matched
// by prefix, not exact equality — the exact suffix has already drifted between two separate
// exports from the 3D team (seen as both "Crocs_Duet_Sport_Clog.001" and
// "Crocs_Duet_Sport_Clog" with no suffix), and an exact-string match would silently stop
// tinting anything if that happens again with no visible error.
const TINTABLE_MATERIAL_PREFIX = 'Crocs_Duet_Sport_Clog';

// Charm attachment points are read live from named nodes in the clog GLB itself (e.g.
// "charm 1", "charm 2"...) — see findCharmAnchors() below. This hardcoded list is only a
// fallback for a model that doesn't have those marker nodes at all.
const FALLBACK_CHARM_SLOT_POSITIONS = [
  [-0.030, 0.100, 0.055],
  [0.000, 0.108, 0.065],
  [0.030, 0.100, 0.055],
  [-0.030, 0.095, 0.015],
  [0.000, 0.103, 0.020],
  [0.030, 0.095, 0.015],
];
const CHARM_SLOT_SIZE = 0.05; // plane diameter in metres — sized to read clearly at a glance;
// the actual visible shape is whatever the charm's real (background-removed) silhouette is,
// which may or may not fill this whole disc
// Fallback only: used when the real surface normal can't be computed (see
// computeOutwardNormal below) — e.g. a marker that isn't actually touching the mesh surface.
const CHARM_FACING_FALLBACK_TARGET = new THREE.Vector3(0, 0.05, -0.03);

function findCharmAnchors(root) {
  // Force matrices up to date before reading world positions — needed because some exports
  // (this one included) parent everything under a node that itself carries a real rotation
  // (a common exporter trick to convert a Z-up-authored mesh to glTF's required Y-up at
  // render time). A marker's *local* position only means what it looks like once that
  // ancestor rotation is accounted for, so world position is the only frame that's safe to
  // use once we re-parent these onto our own standalone billboard meshes.
  root.updateMatrixWorld(true);
  const found = [];
  root.traverse((obj) => {
    // GLTFLoader sanitizes node names — a "charm 1" node (space, as authored in Blender)
    // arrives here as "charm_1" (underscore). Accept either so a future export using spaces,
    // underscores, or no separator at all still matches.
    const m = /^charm[\s_]*(\d+)$/i.exec((obj.name || '').trim());
    if (m) found.push({ index: Number(m[1]), position: obj.getWorldPosition(new THREE.Vector3()) });
  });
  found.sort((a, b) => a.index - b.index);
  return found.map((f) => f.position);
}

// The marker nodes carry no rotation data, so instead of aiming every charm at one fixed
// guessed point (which looks fine for some slots and puts others nearly edge-on to the
// camera), fire a ray from an interior reference point, through the marker, and out through
// the actual shoe mesh — the surface normal at that exit point is a real, per-slot "outward"
// direction. Falls back to the fixed target only if the ray doesn't hit anything (e.g. a
// marker that isn't actually sitting on the surface).
// The delivered model has small (~26-vertex) indicator-sphere meshes sitting at each charm
// marker position — siblings of the named "charm N" empties, not children of them, so they
// can't be excluded by name/hierarchy. Excluding by vertex count instead: the real clog body
// meshes have thousands of vertices, so a low threshold cleanly separates "real shoe surface"
// from "tiny marker indicator" regardless of how a future export names or nests it.
const MIN_SURFACE_VERTEX_COUNT = 200;
function collectSurfaceMeshes(meshRoot) {
  const meshes = [];
  meshRoot.traverse((o) => {
    if (!o.isMesh) return;
    const vertCount = o.geometry?.attributes?.position?.count || 0;
    if (vertCount >= MIN_SURFACE_VERTEX_COUNT) meshes.push(o);
  });
  return meshes;
}

// Finds the point on the mesh surface closest to `point` by brute-force checking every
// triangle, returning that point plus the triangle's (world-space) face normal. Run once per
// marker at setup time, not per frame, so the O(triangle count) cost is negligible.
function findNearestSurfacePoint(point, meshes) {
  const triangle = new THREE.Triangle();
  const vA = new THREE.Vector3(), vB = new THREE.Vector3(), vC = new THREE.Vector3();
  const closest = new THREE.Vector3();
  let best = null;
  meshes.forEach((mesh) => {
    const geom = mesh.geometry;
    const posAttr = geom.attributes.position;
    const index = geom.index;
    const triCount = index ? index.count / 3 : posAttr.count / 3;
    for (let t = 0; t < triCount; t++) {
      const ia = index ? index.getX(t * 3) : t * 3;
      const ib = index ? index.getX(t * 3 + 1) : t * 3 + 1;
      const ic = index ? index.getX(t * 3 + 2) : t * 3 + 2;
      vA.fromBufferAttribute(posAttr, ia).applyMatrix4(mesh.matrixWorld);
      vB.fromBufferAttribute(posAttr, ib).applyMatrix4(mesh.matrixWorld);
      vC.fromBufferAttribute(posAttr, ic).applyMatrix4(mesh.matrixWorld);
      triangle.set(vA, vB, vC);
      triangle.closestPointToPoint(point, closest);
      const distSq = closest.distanceToSquared(point);
      if (!best || distSq < best.distSq) {
        const normal = new THREE.Vector3();
        triangle.getNormal(normal); // vA/vB/vC are already world-space, so this normal is too
        best = { distSq, point: closest.clone(), normal };
      }
    }
  });
  return best;
}

// The previous approach fired a single ray from a *guessed* interior reference point through
// the marker and read the normal off whatever it hit first. That guess (straight in from the
// shoe's vertical centerline) is wrong for a marker near the toe centerline or the front curve
// — the ray can graze past the intended local surface and hit the *far* interior wall instead,
// coming back with a normal that faces back into the shoe rather than outward (the reported
// "charm mostly inside the clog, cropped" bug). Raycasting-through-solid-geometry is inherently
// fragile that way: which surface it happens to hit first depends on the whole mesh, not just
// the local area around the marker. Finding the closest surface point directly (no raycast, no
// guessed reference) is immune to that failure mode — a marker authored on or near the shoe
// skin always resolves to the true local normal at that skin, regardless of the shoe's overall
// shape or which side of an opening the marker sits near.
// Average position of every vertex across the shoe's surface meshes — a stand-in for "a point
// safely inside the solid shoe body", used only to disambiguate which way a surface normal
// should point (see computeOutwardNormal). Computed once per model load, not per marker.
function computeMeshCentroid(meshRoot) {
  const centroid = new THREE.Vector3();
  const v = new THREE.Vector3();
  let count = 0;
  collectSurfaceMeshes(meshRoot).forEach((mesh) => {
    const posAttr = mesh.geometry.attributes.position;
    for (let i = 0; i < posAttr.count; i++) {
      v.fromBufferAttribute(posAttr, i).applyMatrix4(mesh.matrixWorld);
      centroid.add(v);
      count++;
    }
  });
  return count ? centroid.divideScalar(count) : centroid;
}

// Averaging nearby triangles here (instead of just using the single nearest one) was tried —
// motivated by real evidence that the single-nearest read is noisy on this mesh — but it traded
// one problem for a worse one: the averaged direction, while more internally consistent between
// markers, pointed far enough from the single-triangle reading that at the actual default
// product-camera angle, checked directly, charms went from "visible, if imperfectly placed" to
// "edge-on and effectively invisible." Whatever this mesh's geometric normal technically is at
// these points, the single nearest triangle is the reading that has actually kept charms visible
// and correctly facing across every real check against the default camera — that's the
// higher-priority property here, even on the rounds where it left something else imperfect.
function computeOutwardNormal(markerPos, meshRoot, centroid) {
  const nearest = findNearestSurfacePoint(markerPos, collectSurfaceMeshes(meshRoot));
  if (!nearest) return null;
  const resolved = nearest.normal;
  // The closest surface point can legitimately belong to either side of a thin double-walled
  // shell (this model's charm markers sit right where the outer skin and the inner wall of a
  // perforation rim are only a couple of millimetres apart) — geometric distance alone can't
  // tell outer from inner, and a raw face normal is just as likely to come back pointing into
  // the shoe as out of it. Resolve the sign with a direction hint instead of trusting the
  // triangle's winding: the vector from the whole shoe's vertex centroid (reliably inside the
  // solid body) out to the marker approximates the true outward radial direction on every axis.
  const outwardHint = markerPos.clone().sub(centroid);
  if (outwardHint.lengthSq() > 1e-8 && resolved.dot(outwardHint) < 0) resolved.negate();
  return resolved;
}

// The marker's authored position sometimes sits a little off the true centre of the nearest
// perforation hole (an authoring precision issue, not something worth round-tripping to the 3D
// team over) — leaving a sliver of the actual hole visible next to the charm instead of fully
// covered, the way a real clip-in charm covers the hole it plugs into. This finds the hole's
// real centre directly from the geometry: sample a grid of points in the plane tangent to the
// surface normal, and for each, fire a ray back toward the surface along -normal — a ray that
// hits nothing found a gap in the mesh (the hole); one that hits found solid material.
// Returns { position, normal } — normal is the surface direction re-measured right at that
// position (see below for why), or null if that couldn't be measured reliably. `centroid` is
// the same whole-mesh vertex centroid computeOutwardNormal uses, needed for the same reason:
// disambiguating which side of a thin double wall a raw triangle normal belongs to.
function findNearestHoleCenter(markerPos, normal, meshRoot, centroid) {
  const up = Math.abs(normal.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
  const tangentA = new THREE.Vector3().crossVectors(up, normal).normalize();
  const tangentB = new THREE.Vector3().crossVectors(normal, tangentA).normalize();
  const meshes = collectSurfaceMeshes(meshRoot);

  // This model's perforations turn out to be closely-spaced, irregularly-shaped slots, not
  // small isolated round holes with generous clearance around them — measured directly by
  // scanning the geometry, several markers have another opening (or a large vent, wider than
  // a single perforation) within just 10-15mm. A search window wide enough to comfortably find
  // a hole the marker might be off-centre from is therefore *also* wide enough to wander into
  // that neighbour, or a nearby vent that isn't a snap target at all — both measured causes of
  // reported bugs (a charm centred between two holes; a charm dragged toward a large vent
  // instead of the small perforation next to it). Kept deliberately small — this can only ever
  // nudge the marker a few millimetres, not relocate it to a different opening entirely.
  const SEARCH_RADIUS = 0.008; // metres — wide enough to contain a whole nearby perforation
  const STEPS = 8; // grid half-width in samples; spacing = SEARCH_RADIUS / STEPS = 1mm
  const RAY_START_OFFSET = 0.01; // start well outside the mesh, ray back in along -normal

  // Sample a grid of points in the plane tangent to the surface normal; a ray fired back
  // toward the surface along -normal that hits nothing found a gap (a hole), one that hits
  // found solid material.
  const size = STEPS * 2 + 1;
  const mid = STEPS;
  const offMesh = new Array(size * size).fill(false);
  for (let ix = -STEPS; ix <= STEPS; ix++) {
    for (let iy = -STEPS; iy <= STEPS; iy++) {
      const u = (ix / STEPS) * SEARCH_RADIUS;
      const v = (iy / STEPS) * SEARCH_RADIUS;
      if (u * u + v * v > SEARCH_RADIUS * SEARCH_RADIUS) continue;
      const samplePos = markerPos.clone()
        .addScaledVector(tangentA, u)
        .addScaledVector(tangentB, v)
        .addScaledVector(normal, RAY_START_OFFSET);
      const raycaster = new THREE.Raycaster(samplePos, normal.clone().negate(), 0, RAY_START_OFFSET * 3);
      if (!raycaster.intersectObjects(meshes, false).length) offMesh[(ix + mid) * size + (iy + mid)] = true;
    }
  }

  // Averaging every "off-mesh" sample's position gives the centre of *whatever shape the void
  // happens to be* — for a clean isolated circular hole that's correct, but this model's
  // perforations sit close enough together that the void within one search window can be an
  // elongated or dumbbell-shaped region spanning parts of two adjacent holes joined by a
  // narrow gap, and the average of that shape lands in open space between them, on neither
  // hole (confirmed directly — placing a marker at that average and rendering it showed it
  // sitting in the gap between two real holes).
  // Picking the single *deepest* off-mesh cell (farthest from any solid edge) was the fix for
  // that — immune to the two-holes-joined shape without needing to separate the void into
  // regions first. But "deepest" broke down at one marker sitting on a sharply curved part of
  // the toe (confirmed directly with debug markers rendered on the real mesh): this whole
  // tangent-plane search assumes the surface is locally flat around the marker, and on a patch
  // that curves fast, a point several millimetres away that's genuinely nowhere near the real
  // hole can still read as "off-mesh" (the flat sample plane has drifted off the true curved
  // surface out there, not because there's an actual opening) and outscore the real hole on raw
  // depth. The marker's *own* authored position sits right at that real hole's edge in every
  // case checked — the artist placed it there — so once a cell clears the same depth bar (still
  // required, to reject a sliver 2mm wide or less as seam/sampling noise), prefer whichever one
  // is physically closest to the marker over whichever reads deepest. A real hole close to the
  // marker will still win against a same-depth false reading further out; it just stops a
  // curvature-corrupted distant reading from ever being able to outrank the real, nearby hole.
  // Grid is small (a few hundred cells) so brute force here is trivial — no need for anything
  // cleverer than "check every pair."
  let best = null, bestDepth = -1, bestDistFromCenter = Infinity;
  for (let ix = 0; ix < size; ix++) {
    for (let iy = 0; iy < size; iy++) {
      if (!offMesh[ix * size + iy]) continue;
      // Distance (in grid steps) to the nearest solid cell or the edge of the sampled window —
      // treating the window edge as "unknown, assume solid" keeps a void that runs off the edge
      // of the search radius (a large vent, not a small hole) from reading as arbitrarily deep
      // just because we stopped sampling before reaching its actual boundary.
      let depth = Math.min(ix, size - 1 - ix, iy, size - 1 - iy);
      for (let jx = 0; jx < size; jx++) {
        for (let jy = 0; jy < size; jy++) {
          if (offMesh[jx * size + jy]) continue;
          const d = Math.max(Math.abs(jx - ix), Math.abs(jy - iy));
          if (d < depth) depth = d;
        }
      }
      // A depth of 0-1 grid steps (a sliver 2mm wide or less) isn't a real perforation a charm
      // is meant to plug into — likely a seam or sampling noise.
      if (depth < 2) continue;
      const distFromCenter = Math.hypot(ix - mid, iy - mid);
      if (distFromCenter < bestDistFromCenter) { bestDistFromCenter = distFromCenter; bestDepth = depth; best = [ix, iy]; }
    }
  }
  if (!best) return null;

  // The deepest single grid cell is only as precise as the 1mm grid spacing itself, and can sit
  // a little off the hole's true (continuous) centre if the sampled shape isn't perfectly
  // symmetric — visible as a hole still peeking out on one side even though the charm is
  // genuinely attached to the right hole. Averaging every off-mesh cell within a small, tight
  // radius of that validated deepest point recovers sub-grid precision the same way the old
  // whole-window average did for a clean isolated hole — but a *second* hole close enough to
  // bridge into the search window is (by construction, since this cell was picked for being the
  // single deepest point) farther from here than from the window's original centre, so this
  // tight local average doesn't reintroduce the two-holes-averaged-together bug this was built
  // to avoid.
  const REFINE_RADIUS = 2; // grid steps (~2mm) — well under typical hole spacing on this model
  let cu = 0, cv = 0, count = 0;
  for (let ix = Math.max(0, best[0] - REFINE_RADIUS); ix <= Math.min(size - 1, best[0] + REFINE_RADIUS); ix++) {
    for (let iy = Math.max(0, best[1] - REFINE_RADIUS); iy <= Math.min(size - 1, best[1] + REFINE_RADIUS); iy++) {
      if (!offMesh[ix * size + iy]) continue;
      if (Math.max(Math.abs(ix - best[0]), Math.abs(iy - best[1])) > REFINE_RADIUS) continue;
      cu += (ix - mid) / STEPS * SEARCH_RADIUS;
      cv += (iy - mid) / STEPS * SEARCH_RADIUS;
      count++;
    }
  }
  const u = count ? cu / count : ((best[0] - mid) / STEPS) * SEARCH_RADIUS;
  const v = count ? cv / count : ((best[1] - mid) / STEPS) * SEARCH_RADIUS;
  const finalPos = markerPos.clone().addScaledVector(tangentA, u).addScaledVector(tangentB, v);

  // Orientation still shouldn't be read at a point inside/near the hole itself — that's the
  // unstable bevel-rim read that broke a marker before. But using the *original* marker's
  // normal for everything doesn't work either: on a sharply curved area, a several-millimetre
  // snap can land on a patch of skin that genuinely faces a different way, and orienting the
  // plane with the stale, pre-snap normal makes it look tilted / half-sunk relative to the
  // surface it's actually resting on (confirmed — that's what was reported here). The fix is
  // sampling points just past the hole's edge, deliberately outside the void — genuinely on
  // the flat skin immediately surrounding this specific hole, not the hole's own interior.
  //
  // A fixed-radius ring around the centre was the first attempt at this, and it measurably
  // failed: these perforations are elongated/irregular, not round, so a circle sized to clear
  // the *short* axis is still deep inside the void along the *long* axis — most ring samples
  // landed back in the hole instead of on skin, and the handful that did hit skin ended up
  // being an arbitrary, unrepresentative corner rather than the surrounding surface. Marching
  // outward from the centre *in each sample direction independently*, stopping as soon as that
  // specific direction first reaches solid material, adapts to however far the void actually
  // extends that way — reliably landing just past the true edge in every direction regardless
  // of the hole's shape.
  const MARCH_STEP = SEARCH_RADIUS / STEPS; // 1mm — matches the grid's own resolution
  const MARCH_MAX = SEARCH_RADIUS * 2.5; // give up past this; a hole this large isn't a small perforation
  const orientNormal = new THREE.Vector3();
  let orientCount = 0;
  const DIRECTIONS = 8;
  for (let k = 0; k < DIRECTIONS; k++) {
    const angle = (k / DIRECTIONS) * Math.PI * 2;
    const dirA = Math.cos(angle), dirB = Math.sin(angle);
    for (let dist = MARCH_STEP; dist <= MARCH_MAX; dist += MARCH_STEP) {
      const point = finalPos.clone().addScaledVector(tangentA, dirA * dist).addScaledVector(tangentB, dirB * dist);
      const checkPos = point.clone().addScaledVector(normal, RAY_START_OFFSET);
      const raycaster = new THREE.Raycaster(checkPos, normal.clone().negate(), 0, RAY_START_OFFSET * 3);
      if (!raycaster.intersectObjects(meshes, false).length) continue; // still void this far out — keep marching
      const nearest = findNearestSurfacePoint(point, meshes);
      if (nearest) {
        // Same ambiguity as computeOutwardNormal, and the same fix: a raw triangle normal here
        // can belong to either side of a thin double wall, so resolve the sign against the
        // whole-mesh centroid before folding it into the average.
        const sampleNormal = nearest.normal;
        const outwardHint = point.clone().sub(centroid);
        if (outwardHint.lengthSq() > 1e-8 && sampleNormal.dot(outwardHint) < 0) sampleNormal.negate();
        orientNormal.add(sampleNormal);
        orientCount++;
      }
      break; // found this direction's edge (or a usable normal failed) — move to the next direction
    }
  }

  return { position: finalPos, normal: orientCount ? orientNormal.normalize() : null };
}

// Where the preview camera sits — the single source of truth for it (initClogBuilder sets the
// actual camera position by copying this, rather than repeating the numbers), since
// orientToNormal also needs this same point to know which way to face every charm. Rotation is
// locked (see controls.enableRotate = false below), so this is the only angle that matters.
// Z is negative here on purpose — measured directly off the model's own bounding box, the toe
// (where every charm marker actually sits) is the negative-Z end and the heel is positive-Z. A
// positive-Z camera position was found to be sitting behind the heel looking toward the toe —
// showing the closed heel/back of the shoe as the "front", with every charm facing away from
// the camera (and therefore culled to invisible, since they're FrontSide) — confirmed directly
// by temporarily re-orienting charms toward a positive-Z point and watching them disappear.
const DEFAULT_VIEW_POINT = new THREE.Vector3(0, 0.19, -0.38);

// Every charm marker's real local surface normal points a genuinely different direction —
// measured directly, from ~25° to ~70°+ off the camera depending on the marker, because the
// shoe's surface curves substantially over the few centimetres between them. Orienting each
// charm to its own local normal (the natural-seeming approach, and the one this went through
// many rounds trying to make work) means each one reads at a different, sometimes very oblique
// angle — flush with the true surface, but some near edge-on and barely legible from the one
// angle a customer actually sees this preview from, now that it no longer rotates (see
// controls.enableRotate above). Since that angle is now fixed and known, every charm can
// instead simply be oriented to face it directly, like a label facing the camera rather than
// lying flush with the surface underneath it — trading a small amount of physical realism
// (checked directly against the earlier surface-flush version: the difference reads as the
// charm standing very slightly proud of the surface, not as visibly wrong) for every marker
// being clearly readable, which is the property that actually matters here.
function orientToNormal(mesh) {
  const toCamera = DEFAULT_VIEW_POINT.clone().sub(mesh.position).normalize();
  // A marker positioned almost exactly below the camera can't use world-up as its up-reference
  // — the cross product degenerates near zero. Fall back to a horizontal reference axis for
  // just those slots so the basis stays well-defined.
  const upHint = Math.abs(toCamera.y) > 0.95 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(0, 1, 0);
  const xAxis = new THREE.Vector3().crossVectors(upHint, toCamera).normalize();
  const yAxis = new THREE.Vector3().crossVectors(toCamera, xAxis).normalize();
  const basis = new THREE.Matrix4().makeBasis(xAxis, yAxis, toCamera);
  mesh.quaternion.setFromRotationMatrix(basis);
  // No extra flip here — CircleGeometry's front face (the one a FrontSide material renders)
  // sits on local +Z, the same axis this basis points straight at the camera, confirmed by
  // direct A/B test (a plain circle built with exactly this basis and no correction rendered
  // correctly under FrontSide; adding a 180° flip on top of it made it vanish). An earlier
  // version of this function added a flip here based on a similar-looking test against the
  // *previous* (surface-normal-based) basis, which wasn't a safe result to carry over — it
  // turned out to depend on which specific direction that basis's Z axis happened to be built
  // from, not on a fixed property of the geometry itself.
}

// Step definitions. "kind: single" is the existing one-colour-at-a-time swatch UI; "kind:
// multi" is the charm grid (pick several, up to maxCharms). configKey points at the matching
// array in the JSON data island. Only steps whose array is non-empty get a tab, so wiring up
// a future "sockees" step is just a matter of adding data — no layout changes needed.
const STEP_DEFS = [
  { key: 'clog', kind: 'single', configKey: 'clogColors', tabLabel: 'Clog', stepText: 'Choose your clog colour' },
  { key: 'strap', kind: 'single', configKey: 'strapColors', tabLabel: 'Strap', stepText: 'Choose your strap colour' },
  { key: 'charm', kind: 'multi', configKey: 'charms', tabLabel: 'Charms', stepText: 'Choose your charms' },
];

function initClogBuilder(root) {
  const dataEl = root.querySelector('[data-ccb-json]');
  let config;
  try {
    config = JSON.parse(dataEl.textContent);
  } catch (err) {
    // Fail loudly instead of hanging forever on "Loading preview…" with no clue why — this
    // almost always means the Liquid that builds this JSON emitted something invalid (e.g. a
    // stray trailing comma). Check the raw text logged below.
    const statusEl = root.querySelector('[data-ccb-status]');
    if (statusEl) statusEl.textContent = 'Builder failed to load — invalid config JSON (see console).';
    console.error('[custom-clog-builder] JSON.parse failed on data island:', err, dataEl.textContent);
    return;
  }

  // Real embedded 3D charm models (one GLB per "3D Charm model" block, each already baked with
  // the exact position/rotation/scale for one specific marker — see the block schema) take over
  // the whole Charms step when any are configured, replacing the flat-billboard system below
  // entirely rather than living alongside it. That's a deliberate interim state: for now there
  // are only as many real 3D charms as there are markers, so there's no "pick any of 100
  // designs" to offer yet — just a straight on/off toggle per available real model. The
  // billboard system (categories, quantity, hundreds of catalog designs) stays intact under
  // this and comes back automatically the moment no "3D Charm model" blocks are configured.
  const charm3DModels = Array.isArray(config.charm3DModels) ? config.charm3DModels : [];
  const is3DCharmMode = charm3DModels.length > 0;

  // Total charm *quantity* a customer can pick — not distinct charms; the same charm picked
  // twice counts twice. Deliberately independent of how many physical marker slots the model
  // has (see PREVIEW_SLOT_COUNT below, set once the model loads): a customer can buy more
  // charms than the 3D preview has room to show at once, same as buying multiples of anything
  // else — the preview just shows the first few, and the rest still add to cart normally.
  // In 3D-charm mode there's no "buy more than can be shown" case at all — every real model is
  // both a purchasable charm and a guaranteed spot on the shoe, one-to-one — so the cap is just
  // "however many real models exist" rather than a separately configured number.
  const maxCharms = is3DCharmMode ? charm3DModels.length : (Number(config.maxCharms) || 6);
  // How many of the selected charms actually render on the shoe — capped at the model's real
  // marker count once it loads (see buildCharmSlots' return value below). Starts equal to
  // maxCharms so nothing looks artificially limited before the model's loaded.
  let previewSlotCount = maxCharms;

  // Charms are grouped into categories (one per real Shopify collection the merchant picked in
  // the theme editor — see the "Charm category" blocks in the section schema), not one flat
  // list. If the same product is listed in more than one category's collection, both category
  // tabs need to show it pointing at the *same* object — `selected.charm.includes(charm)`
  // (used throughout for selection state) is a reference check, so two separately-parsed copies
  // of "the same" charm would make it possible to select the product twice under two different
  // objects. Routing every category's charms through one productId → object map keeps a single
  // canonical instance no matter how many categories list it.
  const steps = STEP_DEFS
    .map((def) => {
      if (def.key !== 'charm') return { ...def, items: config[def.configKey] || [] };
      if (is3DCharmMode) return { ...def, items: charm3DModels, categories: [] };
      const rawCategories = config.charmCategories || [];
      const byId = new Map();
      const items = [];
      rawCategories.forEach((cat) => {
        (cat.charms || []).forEach((c) => {
          if (byId.has(c.productId)) return;
          byId.set(c.productId, c);
          items.push(c);
        });
      });
      const categories = rawCategories
        .map((cat) => ({ name: cat.name, charms: (cat.charms || []).map((c) => byId.get(c.productId)).filter(Boolean) }))
        .filter((cat) => cat.charms.length);
      return { ...def, items, categories };
    })
    .filter((step) => step.items.length);

  if (!steps.length) {
    const statusEl = root.querySelector('[data-ccb-status]');
    if (statusEl) statusEl.textContent = 'No colours configured yet — add Clog/Strap colour blocks in the theme editor.';
    return;
  }

  const viewer = root.querySelector('[data-ccb-viewer]');
  const statusEl = root.querySelector('[data-ccb-status]');
  const tabsEl = root.querySelector('[data-ccb-tabs]');
  const stepLabelEl = root.querySelector('[data-ccb-step-label]');
  const selectionLineEl = root.querySelector('[data-ccb-selection-line]');
  const swatchRowEl = root.querySelector('[data-ccb-swatches]');
  const charmCategoriesEl = root.querySelector('[data-ccb-charm-categories]');
  const charmGridEl = root.querySelector('[data-ccb-charm-grid]');
  const totalEl = root.querySelector('[data-ccb-total]');
  const addBtn = root.querySelector('[data-ccb-add]');
  const fullscreenBtn = root.querySelector('[data-ccb-fullscreen]');

  const selected = {}; // single steps: one item object. multi steps: array of item objects.
  steps.forEach((step) => { selected[step.key] = step.kind === 'multi' ? [] : step.items[0]; });
  let activeStepIndex = 0;
  let activeCharmCategoryIndex = 0;

  function money(n) {
    return config.currencySymbol + Number(n).toLocaleString();
  }

  function currentTotal() {
    return steps.reduce((sum, step) => {
      if (step.kind === 'multi') {
        return sum + selected[step.key].reduce((s, entry) => s + Number(entry.charm.price) * entry.qty, 0);
      }
      return sum + Number(selected[step.key].price);
    }, 0);
  }

  // Total charm units selected (the same charm picked 3 times counts as 3), as opposed to
  // `selected.charm.length`, which is the number of *distinct* charms picked — the cap below is
  // on total units, matching "pick up to N charms" the way a customer reads it (quantities and
  // all), not "pick up to N different designs".
  function totalCharmQty() {
    return selected.charm.reduce((sum, entry) => sum + entry.qty, 0);
  }

  function renderTabs() {
    tabsEl.innerHTML = '';
    steps.forEach((step, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ccb-tab' + (i === activeStepIndex ? ' is-active' : '');
      btn.textContent = step.tabLabel;
      if (i === activeStepIndex) {
        const check = document.createElement('span');
        check.className = 'ccb-tab-check';
        check.textContent = '✓';
        btn.appendChild(check);
      }
      btn.addEventListener('click', () => {
        activeStepIndex = i;
        renderTabs();
        renderStep();
      });
      tabsEl.appendChild(btn);
    });
  }

  function renderStep() {
    const step = steps[activeStepIndex];
    stepLabelEl.textContent = `Step ${activeStepIndex + 1}: ${step.stepText}`;

    if (step.kind === 'single') {
      swatchRowEl.classList.remove('ccb-hidden');
      charmCategoriesEl.classList.add('ccb-hidden');
      charmGridEl.classList.add('ccb-hidden');

      selectionLineEl.innerHTML = `Colour: <strong>${selected[step.key].name}</strong><span class="ccb-color-check">✓</span>`;

      swatchRowEl.innerHTML = '';
      step.items.forEach((c) => {
        const el = document.createElement('button');
        el.type = 'button';
        el.className = 'ccb-swatch' + (selected[step.key] === c ? ' is-active' : '');
        el.style.background = c.hex;
        el.title = c.name;
        el.addEventListener('click', () => {
          selected[step.key] = c;
          setPartColor(step.key, c.hex);
          renderStep();
          renderTotal();
        });
        swatchRowEl.appendChild(el);
      });
    } else {
      swatchRowEl.classList.add('ccb-hidden');
      charmGridEl.classList.remove('ccb-hidden');

      const count = totalCharmQty();
      let line = count ? `${count} of ${maxCharms} selected` : `Pick up to ${maxCharms} charms`;
      // Only worth mentioning when it actually limits anything — a merchant whose max is at or
      // below the real marker count never hits this, so nothing here would ever be true for them.
      if (maxCharms > previewSlotCount) line += ` (first ${previewSlotCount} shown in the 3D preview)`;
      selectionLineEl.textContent = line;

      renderCharmCategories(step);
      renderCharmGrid(step);
    }
  }

  // Category pills (one per "Charm category" block/collection the merchant configured) sit
  // above the grid, matching the Figma. A merchant who hasn't set any up yet still gets exactly
  // today's single flat grid — `step.categories` is only populated when there's real category
  // data (see the STEP_DEFS mapping above), so this row just stays hidden in that case rather
  // than showing a single meaningless "all charms" pill.
  function renderCharmCategories(step) {
    const categories = step.categories || [];
    if (categories.length < 2) {
      charmCategoriesEl.classList.add('ccb-hidden');
      charmCategoriesEl.innerHTML = '';
      return;
    }
    if (activeCharmCategoryIndex >= categories.length) activeCharmCategoryIndex = 0;
    charmCategoriesEl.classList.remove('ccb-hidden');
    charmCategoriesEl.innerHTML = '';
    categories.forEach((cat, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ccb-charm-category' + (i === activeCharmCategoryIndex ? ' is-active' : '');
      btn.textContent = cat.name;
      btn.addEventListener('click', () => {
        if (activeCharmCategoryIndex === i) return;
        activeCharmCategoryIndex = i;
        renderCharmCategories(step);
        renderCharmGrid(step);
      });
      charmCategoriesEl.appendChild(btn);
    });
  }

  function renderCharmGrid(step) {
    const categories = step.categories || [];
    const pool = categories.length >= 2 ? (categories[activeCharmCategoryIndex] || categories[0]).charms : step.items;

    charmGridEl.innerHTML = '';
    const totalQty = totalCharmQty();
    const atCap = totalQty >= maxCharms;

    pool.forEach((charm) => {
      const entry = findCharmEntry(charm);
      const qty = entry ? entry.qty : 0;
      const isSelected = qty > 0;

      const el = document.createElement('div');
      el.className = 'ccb-charm-item' + (isSelected ? ' is-selected' : '');

      const thumbBtn = document.createElement('button');
      thumbBtn.type = 'button';
      thumbBtn.className = 'ccb-charm-thumb-btn' + (!isSelected && atCap ? ' is-disabled' : '');
      thumbBtn.title = charm.name;
      thumbBtn.disabled = !isSelected && atCap;

      const thumb = document.createElement('div');
      thumb.className = 'ccb-charm-thumb';
      if (charm.image) {
        const img = document.createElement('img');
        img.src = charm.image;
        img.loading = 'lazy';
        img.alt = charm.name;
        thumb.appendChild(img);
      }
      if (isSelected) {
        const check = document.createElement('span');
        check.className = 'ccb-charm-check';
        check.textContent = '✓';
        thumb.appendChild(check);
      }
      thumbBtn.appendChild(thumb);
      // Clicking the image itself while selected removes it outright, quantity and all — a
      // quick "never mind" that doesn't require dialing the stepper back down one at a time.
      thumbBtn.addEventListener('click', () => (isSelected ? setCharmQty(charm, 0) : incrementCharm(charm)));
      el.appendChild(thumbBtn);

      // No quantity stepper in 3D-charm mode — each real model is a unique, one-of-one object
      // baked to a specific marker, so "2 of the same one" isn't a meaningful thing to offer;
      // it's a plain on/off toggle there, same as clicking the thumb already does above.
      if (isSelected && !is3DCharmMode) {
        const qtyRow = document.createElement('div');
        qtyRow.className = 'ccb-charm-qty';

        const dec = document.createElement('button');
        dec.type = 'button';
        dec.className = 'ccb-qty-btn';
        dec.textContent = '−';
        dec.setAttribute('aria-label', 'Decrease quantity');
        dec.addEventListener('click', () => decrementCharm(charm));

        const val = document.createElement('span');
        val.className = 'ccb-qty-value';
        val.textContent = String(qty);

        const inc = document.createElement('button');
        inc.type = 'button';
        inc.className = 'ccb-qty-btn';
        inc.textContent = '+';
        inc.setAttribute('aria-label', 'Increase quantity');
        inc.disabled = atCap;
        inc.addEventListener('click', () => incrementCharm(charm));

        qtyRow.appendChild(dec);
        qtyRow.appendChild(val);
        qtyRow.appendChild(inc);
        el.appendChild(qtyRow);
      }

      charmGridEl.appendChild(el);
    });
  }

  function findCharmEntry(charm) {
    return selected.charm.find((entry) => entry.charm === charm);
  }

  function setCharmQty(charm, qty) {
    const entry = findCharmEntry(charm);
    if (qty <= 0) {
      if (entry) selected.charm.splice(selected.charm.indexOf(entry), 1);
    } else if (entry) {
      entry.qty = qty;
    } else {
      selected.charm.push({ charm, qty });
    }
    assignCharmSlots();
    renderStep();
    renderTotal();
  }

  function incrementCharm(charm) {
    if (totalCharmQty() >= maxCharms) return;
    const entry = findCharmEntry(charm);
    setCharmQty(charm, (entry ? entry.qty : 0) + 1);
  }

  function decrementCharm(charm) {
    const entry = findCharmEntry(charm);
    if (!entry) return;
    setCharmQty(charm, entry.qty - 1);
  }

  function renderTotal() {
    totalEl.textContent = money(currentTotal());
  }

  // --- Three.js scene setup ---
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xf4f3f6);

  const camera = new THREE.PerspectiveCamera(35, viewer.clientWidth / viewer.clientHeight, 0.01, 10);
  camera.position.copy(DEFAULT_VIEW_POINT);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(viewer.clientWidth, viewer.clientHeight);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.shadowMap.enabled = true;
  viewer.appendChild(renderer.domElement);

  const controls = new OrbitControls(camera, renderer.domElement);
  // Aimed at the toe box specifically (where the charm markers actually are), not the heel —
  // see the note on DEFAULT_VIEW_POINT above for how the earlier version ended up on the wrong
  // side of the shoe.
  controls.target.set(0, 0.075, -0.08);
  controls.enableDamping = true;
  controls.minDistance = 0.15;
  controls.maxDistance = 1.2;
  if (is3DCharmMode) {
    // Real embedded 3D charm meshes read correctly from any angle — checked directly across a
    // full orbit — because they're actual geometry sitting flush on the surface, not a flat
    // image forced to face one fixed point. None of the reasoning below (which is specific to
    // the billboard system) applies here, so rotation stays fully free.
    controls.enableRotate = true;
    controls.enablePan = false;
    controls.update();
  } else {
    // Full 360° rotation is locked out on purpose. Every charm's real local surface normal
    // points a genuinely different direction (measured directly — some barely 25° off the
    // camera, others past 70°), because the marker positions sit across a shoe surface that
    // curves a lot over a few centimetres. There is no single orientation rule that reads
    // clearly from every possible angle *and* stays flush with that varying curvature — chasing
    // one across many rounds kept fixing whichever marker was being tested while quietly
    // breaking another. Fixing the *left/right* angle turns that into a solvable problem: every
    // charm can simply be oriented to face this one specific, known camera position (see
    // orientToNormal), which guarantees all of them read clearly from the angle a customer
    // actually sees.
    // Up/down tilt is still allowed within a modest range — unlike full orbit, a limited tilt
    // doesn't move far enough off DEFAULT_VIEW_POINT to make any charm unreadable, and it lets a
    // customer glance at the toe from slightly above or below without feeling locked rigid.
    controls.enableRotate = true;
    controls.update();
    const lockedAzimuth = controls.getAzimuthalAngle();
    controls.minAzimuthAngle = lockedAzimuth;
    controls.maxAzimuthAngle = lockedAzimuth;
    const centerPolarAngle = controls.getPolarAngle();
    const VERTICAL_TILT_RANGE = THREE.MathUtils.degToRad(20);
    controls.minPolarAngle = centerPolarAngle - VERTICAL_TILT_RANGE;
    controls.maxPolarAngle = centerPolarAngle + VERTICAL_TILT_RANGE;
    // Panning would slide the camera sideways off this same fixed angle just as surely as
    // left/right rotation would — disabled for the same reason. Zoom is left on: it moves the
    // camera closer or farther along the same viewing direction, which doesn't change which way
    // anything faces.
    controls.enablePan = false;
    controls.update();
  }

  scene.add(new THREE.HemisphereLight(0xffffff, 0x666666, 1.2));
  const key = new THREE.DirectionalLight(0xffffff, 2.2);
  key.position.set(0.6, 1, 0.4);
  key.castShadow = true;
  key.shadow.mapSize.set(1024, 1024);
  // Tight frustum fit to the object — the Three.js default is far too large for a ~0.3m
  // object and starves the shadow map of resolution, causing moire/"shadow acne".
  key.shadow.camera.left = -0.25;
  key.shadow.camera.right = 0.25;
  key.shadow.camera.top = 0.25;
  key.shadow.camera.bottom = -0.25;
  key.shadow.camera.near = 0.1;
  key.shadow.camera.far = 2;
  key.shadow.bias = -0.0015;
  key.shadow.normalBias = 0.002;
  scene.add(key);
  const fill = new THREE.DirectionalLight(0xffffff, 0.8);
  fill.position.set(-0.6, 0.4, -0.4);
  scene.add(fill);

  const ground = new THREE.Mesh(
    new THREE.CircleGeometry(1, 64),
    new THREE.ShadowMaterial({ opacity: 0.18 })
  );
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  scene.add(ground);

  const loader = new GLTFLoader();
  const tintableMeshesByPart = { clog: [], strap: [] };
  let clogRoot = null;

  function loadPart(url, partKey) {
    return new Promise((resolve, reject) => {
      loader.load(url, (gltf) => {
        const partRoot = gltf.scene;
        partRoot.traverse((obj) => {
          if (!obj.isMesh) return;
          obj.castShadow = true;
          obj.receiveShadow = true;
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach((mat) => {
            if (mat && mat.name && mat.name.startsWith(TINTABLE_MATERIAL_PREFIX)) {
              const cloned = mat.clone();
              // Strip the baked-in "gem-cut" normal map from the source model so the surface
              // renders as plain smooth plastic instead of that branded facet texture.
              cloned.normalMap = null;
              cloned.needsUpdate = true;
              if (Array.isArray(obj.material)) {
                obj.material = obj.material.map((m) => (m === mat ? cloned : m));
              } else {
                obj.material = cloned;
              }
              tintableMeshesByPart[partKey].push(cloned);
            }
          });
        });
        scene.add(partRoot);
        if (partKey === 'clog') clogRoot = partRoot;
        resolve(partRoot);
      }, undefined, reject);
    });
  }

  function setPartColor(partKey, hex) {
    // A true #000000 has zero diffuse reflectance under PBR lighting, so it renders as a
    // flat, detail-less silhouette instead of looking like a real black material. Floor each
    // channel slightly for the 3D preview only — the swatch, colour name, and cart payload
    // all still use the real hex value, this only affects how dark the render can go.
    const c = new THREE.Color(hex);
    const floor = 0.05;
    c.r = Math.max(c.r, floor);
    c.g = Math.max(c.g, floor);
    c.b = Math.max(c.b, floor);
    tintableMeshesByPart[partKey].forEach((mat) => mat.color.copy(c));
  }

  // --- Charm billboards: small circular image planes pinned to fixed anchor points on the
  // clog body. One reusable plane per slot, shown/hidden and re-textured as charms are
  // toggled — this is what lets 100+ charm designs work with zero 3D modelling per charm. ---
  const charmSlotMeshes = [];
  const charmTextureCache = new Map(); // productId -> THREE.CanvasTexture

  function buildCharmSlots() {
    const anchors = clogRoot ? findCharmAnchors(clogRoot) : [];
    const positions = anchors.length ? anchors : FALLBACK_CHARM_SLOT_POSITIONS.map((p) => new THREE.Vector3(...p));
    if (!anchors.length) {
      console.warn('[custom-clog-builder] no "charm N" marker nodes found in the clog model — using approximate fallback positions.');
    }
    const slotGeometry = new THREE.CircleGeometry(CHARM_SLOT_SIZE / 2, 24);
    const slotCount = Math.min(maxCharms, positions.length);
    // Computed once and reused for every marker — see computeOutwardNormal for why this is
    // needed (disambiguating which side of a thin double wall a surface normal belongs to).
    const centroid = anchors.length ? computeMeshCentroid(clogRoot) : null;
    for (let i = 0; i < slotCount; i++) {
      const pos = positions[i];
      const mat = new THREE.MeshBasicMaterial({
        transparent: true, depthWrite: false,
        // Front-facing only, not DoubleSide. This model's perforations are real holes through
        // to the shoe's hollow interior, and a double-sided plane sitting just outside one is
        // visible through it from inside the cavity — its back face, showing right through the
        // hole. That's not the charm being embedded in the material (checked directly: from the
        // normal, front-facing viewing angle, full size, it sits perfectly flush), but it reads
        // exactly like it at a glance, and a customer has no reason to ever view the inside of
        // their own shoe anyway. Rendering only the front face removes that view entirely.
        side: THREE.FrontSide,
        opacity: 0, // invisible until a texture actually loads — no placeholder circle if a charm image fails
      });
      const mesh = new THREE.Mesh(slotGeometry, mat);
      mesh.position.copy(pos);
      // The real surface normal here is still used for *position* — standing the plane off the
      // shoe by the right amount and in the right direction to actually clear the surface (see
      // computeOutwardNormal) — even though orientToNormal no longer uses it for facing (see
      // that function for why: with the preview's rotation now locked, every charm faces the one
      // fixed camera position directly instead of its own local surface direction).
      const originalNormal = anchors.length ? computeOutwardNormal(pos, clogRoot, centroid) : null;
      if (originalNormal) {
        // Snap sideways onto the real centre of the nearest perforation hole, if there is one
        // nearby — the authored marker position is sometimes a little off, which otherwise
        // leaves a sliver of the hole visible next to the charm instead of fully covered.
        const hole = findNearestHoleCenter(pos, originalNormal, clogRoot, centroid);
        const holePosition = hole ? hole.position : pos;
        // Standoff direction: toward the fixed camera point, not along originalNormal. That
        // normal is measured off whichever real mesh triangle sits nearest the hole, and at a
        // handful of markers (confirmed directly — one on the toe's side curve rendered visibly
        // sunk, no gap around its hole, next to other markers that stood proud correctly) the
        // nearest triangle turns out to belong to a slightly different facet than the one the
        // camera is actually looking down onto, so offsetting along it doesn't reliably move the
        // charm toward the viewer. Since the viewing angle is fixed (see DEFAULT_VIEW_POINT),
        // "toward the camera" is a direction that's always correct for this one purpose —
        // guaranteeing the charm ends up on the visible side of the surface, standing proud —
        // the same reasoning already applied to orientToNormal's facing direction below.
        const toCameraDir = DEFAULT_VIEW_POINT.clone().sub(holePosition).normalize();
        mesh.position.copy(holePosition).addScaledVector(toCameraDir, CHARM_SLOT_SIZE * 0.28);
        orientToNormal(mesh);
      } else {
        mesh.lookAt(CHARM_FACING_FALLBACK_TARGET);
      }
      mesh.visible = false;
      mesh.renderOrder = 10;
      (clogRoot || scene).add(mesh);
      charmSlotMeshes.push(mesh);
    }
    shrinkCrowdedSlots(charmSlotMeshes);
    return slotCount;
  }

  // Two of this model's charm markers sit only ~32mm apart (measured directly) — closer than
  // the ~42mm a full-size charm disc needs, so at full size they don't just look crowded on
  // screen, their discs genuinely overlap in 3D regardless of viewing angle. Each charm still
  // has to stay centred on its own real hole (that's the whole point of the snapping above), so
  // the only lever here is size: shrink just the slots that are actually too close to a
  // neighbour, just enough to clear it, and leave every other slot at full size.
  // How far that shrink is allowed to go is capped higher than "still reads clearly" alone would
  // suggest — checked directly against one of these two markers with a wordmark-style charm
  // (mostly open space between and inside the letters): at ~73% scale the real hole showed
  // through those internal gaps even though the disc was still correctly centred on it, and only
  // stopped at 85%+. A charm's own artwork can't be relied on to have solid coverage everywhere
  // inside its bounding circle, so the floor has to protect hole coverage, not just legibility.
  const MIN_CENTER_GAP = CHARM_SLOT_SIZE * 0.95;
  const MIN_SCALE = 0.85;
  function shrinkCrowdedSlots(meshes) {
    const scales = new Array(meshes.length).fill(1);
    for (let a = 0; a < meshes.length; a++) {
      for (let b = a + 1; b < meshes.length; b++) {
        const dist = meshes[a].position.distanceTo(meshes[b].position);
        if (dist >= MIN_CENTER_GAP) continue;
        const factor = Math.max(MIN_SCALE, dist / MIN_CENTER_GAP);
        scales[a] = Math.min(scales[a], factor);
        scales[b] = Math.min(scales[b], factor);
      }
    }
    meshes.forEach((mesh, i) => { if (scales[i] < 1) mesh.scale.setScalar(scales[i]); });
  }

  // Product photos often have TWO layers of non-charm backdrop: the plain studio background,
  // and — some of these specifically — a separate light grey/off-white mounting card the charm
  // is physically shot sitting on, plus sometimes a soft grey drop shadow. Classifying by "is
  // this light and essentially colourless" rather than "does this match one exact sampled
  // colour" catches all of that in one pass, since none of it has real charm colour in it —
  // while an enclosed light detail that's actually part of the charm (e.g. a shark's white
  // teeth) survives, since the flood fill can only remove pixels reachable from the image
  // border through other qualifying pixels. This makes the background genuinely transparent —
  // it does NOT invent a backing plate behind the charm. Not every charm has a solid backing
  // disc in real life (some are flat printed shapes with no base at all), so guessing one
  // universally was wrong; whatever the charm's real silhouette is, that's what shows.
  function removeStudioBackground(ctx, width, height) {
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    const LIGHTNESS_HARD = 232; // fully backing at/above this lightness (with low saturation)
    const DARK_PROTECT = 90; // kept as real charm material at/below this — protects a solid
    // dark charm backing already present in the photo from being treated as shadow.
    // Everything between fades smoothly — wide on purpose, so a product photo's soft grey drop
    // shadow blends into the backing gradually instead of acting as a hard wall the flood fill
    // can't cross, which otherwise traps background pixels behind it as an unreachable island.
    const SATURATION_MAX = 0.16; // above this, treat as real charm colour regardless of lightness

    function keepFactor(i) {
      // A pixel the source image never actually covered (canvas letterboxing from a
      // contain-fit on a non-square photo) is blank (0,0,0,0), not real black — treat it as
      // trivially background, or it reads identically to a genuine black charm base and forms
      // a wall the flood fill can never cross, trapping everything inside it as unreachable.
      if (data[i + 3] === 0) return 0;
      const r = data[i], g = data[i + 1], b = data[i + 2];
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      const saturation = max === 0 ? 0 : (max - min) / max;
      if (saturation > SATURATION_MAX) return 1; // real colour — always kept as-is
      if (max <= DARK_PROTECT) return 1;
      if (max >= LIGHTNESS_HARD) return 0;
      return 1 - (max - DARK_PROTECT) / (LIGHTNESS_HARD - DARK_PROTECT);
    }

    const visited = new Uint8Array(width * height);
    const stack = [];
    for (let x = 0; x < width; x++) { stack.push(x, 0, x, height - 1); }
    for (let y = 0; y < height; y++) { stack.push(0, y, width - 1, y); }

    while (stack.length) {
      const y = stack.pop();
      const x = stack.pop();
      if (x < 0 || y < 0 || x >= width || y >= height) continue;
      const p = y * width + x;
      if (visited[p]) continue;
      visited[p] = 1;
      const i = p * 4;
      const keep = keepFactor(i);
      if (keep >= 1) continue; // hit the real charm — don't cross into it
      data[i + 3] = Math.round(data[i + 3] * keep); // genuinely transparent — no invented backing
      stack.push(x + 1, y, x - 1, y, x, y + 1, x, y - 1);
    }
    ctx.putImageData(imageData, 0, 0);
  }

  function circleCropTexture(imageUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const size = 256;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        // Contain-fit (not cover-fit) — a charm's real silhouette needs to be fully visible,
        // not cropped to fill a circle the way the old white-background version was.
        const scale = Math.min(size / img.width, size / img.height) * 0.9;
        const w = img.width * scale;
        const h = img.height * scale;
        ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
        removeStudioBackground(ctx, size, size);
        const texture = new THREE.CanvasTexture(canvas);
        texture.colorSpace = THREE.SRGBColorSpace;
        resolve(texture);
      };
      img.onerror = reject;
      img.src = imageUrl;
    });
  }

  // Expands {charm, qty} entries into one list entry per physical unit, in selection order —
  // e.g. picking 3 of charm A then 2 of charm B flattens to [A, A, A, B, B]. Only the first
  // `charmSlotMeshes.length` units get an actual slot on the shoe (see the loop below); a
  // customer can select more total charms than there are markers to show them on, and the rest
  // just don't appear in the 3D preview while still adding to cart normally.
  function flattenedCharmUnits() {
    const units = [];
    selected.charm.forEach((entry) => {
      for (let i = 0; i < entry.qty; i++) units.push(entry.charm);
    });
    return units;
  }

  // Dispatches to whichever charm rendering path is active — see is3DCharmMode above. Every
  // call site (the qty helpers, the initial model-load handler) calls this one function and
  // doesn't need to know which mode is live.
  function assignCharmSlots() {
    if (is3DCharmMode) { assignCharm3DModels(); return; }
    assignBillboardCharmSlots();
  }

  function assignBillboardCharmSlots() {
    const units = flattenedCharmUnits();
    units.forEach((charm, i) => {
      const mesh = charmSlotMeshes[i];
      if (!mesh) return; // beyond the model's real marker count — still added to cart, just not shown here
      mesh.visible = true;
      if (charmTextureCache.has(charm.productId)) {
        mesh.material.map = charmTextureCache.get(charm.productId);
        mesh.material.opacity = 1;
        mesh.material.needsUpdate = true;
        return;
      }
      if (!charm.image) return;
      circleCropTexture(charm.image).then((texture) => {
        charmTextureCache.set(charm.productId, texture);
        mesh.material.map = texture;
        mesh.material.opacity = 1;
        mesh.material.needsUpdate = true;
      }).catch((err) => {
        console.warn('[custom-clog-builder] charm image failed to load for 3D preview:', charm.name, err);
      });
    });
    // Hide any slots beyond the current selection count.
    for (let i = units.length; i < charmSlotMeshes.length; i++) {
      if (charmSlotMeshes[i]) charmSlotMeshes[i].visible = false;
    }
  }

  // --- Real 3D charm models (see is3DCharmMode above) ---
  // Each model is loaded exactly once, up front, and kept in the scene permanently — there's
  // no texture-swapping or repositioning to do (unlike the billboard slots above) because each
  // file already carries its own baked position for one specific marker. Selecting a charm is
  // just toggling that one object's visibility.
  const charm3DObjects = [];
  function loadCharm3DModels() {
    charm3DModels.forEach((item, i) => {
      new GLTFLoader().load(item.modelUrl, (gltf) => {
        gltf.scene.traverse((obj) => {
          if (obj.isMesh) { obj.castShadow = true; obj.receiveShadow = true; }
        });
        gltf.scene.visible = !!findCharmEntry(item); // covers a selection made before this file finished loading
        charm3DObjects[i] = gltf.scene;
        clogRoot.add(gltf.scene);
      }, undefined, (err) => {
        console.error('[custom-clog-builder] failed to load 3D charm model:', item.modelUrl, err);
      });
    });
  }

  function assignCharm3DModels() {
    charm3DModels.forEach((item, i) => {
      const obj = charm3DObjects[i];
      if (!obj) return; // still loading — loadCharm3DModels sets its initial visibility itself
      obj.visible = !!findCharmEntry(item);
    });
  }

  // Tabs, swatches/grid, and cart total all work independently of whether the 3D preview
  // manages to load — a broken/missing model file should never take down the rest of the UI.
  renderTabs();
  renderStep();
  renderTotal();

  Promise.all([
    loadPart(config.clogModelUrl, 'clog'),
    loadPart(config.strapModelUrl, 'strap'),
  ]).then(() => {
    statusEl.textContent = '';
    steps.forEach((step) => {
      if (step.kind === 'single') setPartColor(step.key, selected[step.key].hex);
    });
    if (is3DCharmMode) {
      loadCharm3DModels();
    } else {
      previewSlotCount = buildCharmSlots();
      if (previewSlotCount !== maxCharms) {
        // Re-render so the "(first N shown in the 3D preview)" note (see renderStep) reflects
        // the model's real marker count instead of the placeholder it started with, and so any
        // charms already picked before the model finished loading get assigned into the
        // now-known-correct slot count.
        assignCharmSlots();
        renderStep();
      }
    }
  }).catch((err) => {
    statusEl.textContent = 'Could not load 3D preview — check that the model URLs are correct.';
    console.error('[custom-clog-builder]', err);
  });

  fullscreenBtn.addEventListener('click', () => {
    if (!document.fullscreenElement) {
      root.querySelector('.ccb-preview').requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  });

  new ResizeObserver(() => {
    if (!viewer.clientWidth || !viewer.clientHeight) return;
    camera.aspect = viewer.clientWidth / viewer.clientHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(viewer.clientWidth, viewer.clientHeight);
  }).observe(viewer);

  (function animate() {
    requestAnimationFrame(animate);
    controls.update();
    renderer.render(scene, camera);
  })();

  // --- Cart: add every configured part as a separate line item (one per charm too), all tied
  // together with a shared property. Always clickable — adds whatever is currently selected
  // across every step, not just the step you're looking at right now. ---
  addBtn.addEventListener('click', async () => {
    addBtn.disabled = true;
    addBtn.textContent = 'Adding…';
    const bundleId = 'clog-build-' + Date.now();
    const items = [];
    steps.forEach((step) => {
      if (step.kind === 'multi') {
        selected[step.key].forEach((entry) => {
          items.push({
            id: Number(entry.charm.variantId),
            quantity: entry.qty,
            properties: { _bundle_id: bundleId, _bundle_part: 'Charm', _bundle_color: entry.charm.name },
          });
        });
      } else {
        const c = selected[step.key];
        items.push({
          id: Number(c.variantId),
          quantity: 1,
          properties: { _bundle_id: bundleId, _bundle_part: step.tabLabel, _bundle_color: c.name },
        });
      }
    });
    try {
      const res = await fetch(config.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Add to cart failed');
      }
      addBtn.textContent = 'Added ✓';
      addBtn.classList.add('is-added');
      window.location.href = config.cartUrl;
    } catch (err) {
      console.error('[custom-clog-builder] add to cart failed', err);
      addBtn.innerHTML = 'Something went wrong — try again';
      addBtn.disabled = false;
      addBtn.classList.remove('is-added');
    }
  });
}

document.querySelectorAll('[data-custom-clog-builder]').forEach(initClogBuilder);
